/**
 * ErrorState — the standard "something went wrong" panel.
 *
 * Pairs with EmptyState (nothing here) and Skeleton (loading). Together these
 * three cover the states every data-driven screen must handle:
 *   loading  -> Skeleton
 *   empty    -> EmptyState
 *   error    -> ErrorState   ← this file
 *   success  -> the real content
 *
 * It takes the normalised error object from apiClient (so `error.message` is
 * always safe to show) and an optional retry callback — React Query gives us a
 * `refetch` function that plugs straight in.
 */

import { AlertTriangle, RefreshCw, WifiOff } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Button } from './Button';

export function ErrorState({ error, onRetry, className }) {
  const isNetwork = error?.isNetworkError;
  const Icon = isNetwork ? WifiOff : AlertTriangle;

  return (
    <div
      className={cn('flex flex-col items-center justify-center px-6 py-16 text-center', className)}
    >
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-50 text-danger">
        <Icon size={30} strokeWidth={1.75} />
      </div>
      <h3 className="text-lg font-semibold text-ink">
        {isNetwork ? 'Connection problem' : 'Something went wrong'}
      </h3>
      <p className="mt-1.5 max-w-sm text-sm text-muted">
        {error?.message ?? 'An unexpected error occurred. Please try again.'}
      </p>
      {onRetry && (
        <Button
          variant="secondary"
          className="mt-6"
          onClick={onRetry}
          leftIcon={<RefreshCw size={16} />}
        >
          Try again
        </Button>
      )}
    </div>
  );
}

export default ErrorState;
